import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { refundsService } from '@/services/refundsService'
import { billsService } from '@/services/billsService'
import {
  RefundVm,
  RefundLine,
  RefundVendor,
  RefundBill,
  RefundBillLine,
  RefundListItem,
  BillItem,
  BillTaxRate,
  BillAccount,
} from '@/api/types'

export const RefundsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const refundFromList = (location.state as any)?.refund as RefundListItem | undefined
  const branchId = user?.selectedBranch?.branchId

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: BillTaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => (t.taxRateId || t.id) === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId || taxById.id || 0,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId || tax15.id || 0,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  const getDefaultRows = (taxRates: BillTaxRate[] = []): RefundLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 1 }, (): RefundLine => ({
      lineId: 0,
      billLineId: 0,
      refundId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      creditAccount: 0,
      isDeleted: 'false',
    }))
  }

  const [refund, setRefund] = useState<RefundVm>({
    refundId: 0,
    refundDate: new Date().toISOString().split('T')[0],
    billId: 0,
    dueDate: '',
    subTotal: 0,
    totalTax: 0,
    total: 0,
    debitAccount: 0,
    isFullyRefund: '',
    note: '',
    branchId: branchId || 0,
    ipAddress: '',
    refundLine: getDefaultRows([]),
  })

  const [vendors, setVendors] = useState<RefundVendor[]>([])
  const [bills, setBills] = useState<RefundBill[]>([])
  const [, setBillLines] = useState<RefundBillLine[]>([])
  const [originalBillLines, setOriginalBillLines] = useState<Map<number, { qty: number; unitPrice: number }>>(new Map())
  const [billsWithValidLines, setBillsWithValidLines] = useState<Set<number>>(new Set())
  const [checkingBillLines, setCheckingBillLines] = useState(false)
  const [items, setItems] = useState<BillItem[]>([])
  const [taxRates, setTaxRates] = useState<BillTaxRate[]>([])
  const [accounts, setAccounts] = useState<BillAccount[]>([])
  const [remainingAmount, setRemainingAmount] = useState<number>(0)
  const [vendorName, setVendorName] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  // Load lookup data
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [vendorsData, billsData, itemsData, taxRatesData, accountsData] = await Promise.all([
          refundsService.getAllVendors(branchId),
          refundsService.getAllBills(branchId),
          billsService.getItems(branchId),
          billsService.getTaxRates(branchId),
          billsService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData)
        setBills(billsData)
        setItems(itemsData)
        setTaxRates(taxRatesData)
        setAccounts(accountsData)
        
        // Check which bills have valid lines (at least one line with qty>0 and amount>0)
        if (billsData && billsData.length > 0) {
          setCheckingBillLines(true)
          const validBillIds = new Set<number>()
          
          // Check each bill's lines
          const checkPromises = billsData.map(async (bill: RefundBill) => {
            const billId = bill.billId || bill.id
            if (!billId) return false
            
            try {
              const response = await refundsService.getBillLines(branchId, billId)
              const lines = response.billsLines || []
              
              // Check if at least one line has qty>0 and amount>0
              const hasValidLine = lines.some((line: RefundBillLine) => {
                const qty = line.qty || 0
                const lineAmount = line.lineAmount || 0
                return qty > 0 && lineAmount > 0
              })
              
              if (hasValidLine) {
                validBillIds.add(billId)
              }
              return hasValidLine
            } catch (error) {
              console.warn(`Failed to check lines for bill ${billId}:`, error)
              // If we can't check, include it to be safe (don't filter out)
              validBillIds.add(billId)
              return true
            }
          })
          
          await Promise.all(checkPromises)
          setBillsWithValidLines(validBillIds)
          setCheckingBillLines(false)
          console.log('Bills with valid lines:', validBillIds.size, 'out of', billsData.length)
        }
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        await showError(t('common.error', { defaultValue: 'Error' }), t('refunds.failedToLoadRefunds'))
        setBillsWithValidLines(new Set())
        setCheckingBillLines(false)
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setRefund(prev => {
      const hasRowsWithoutVAT = prev.refundLine?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.refundLine?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, refundLine: updatedLines }
    })
  }, [taxRates])

  // Load refund data for edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadRefund = async () => {
      try {
        setLoading(true)
        // TODO: Implement getRefundById if API provides it
        // For now, use data from list if available
        if (refundFromList) {
          setRefund((prev) => ({
            ...prev,
            refundId: refundFromList.refundId || Number(id),
            refundDate: refundFromList.refundDate || new Date().toISOString().split('T')[0],
            billId: refundFromList.billId || 0,
            dueDate: refundFromList.dueDate || '',
            subTotal: refundFromList.subTotal || 0,
            totalTax: refundFromList.totalTax || 0,
            total: refundFromList.total || refundFromList.amount || 0,
            note: refundFromList.note || '',
            branchId: branchId,
          }))
        }
      } catch (error: any) {
        console.error('Failed to load refund', error)
        await showError('Error', 'Failed to load refund data')
      } finally {
        setLoading(false)
      }
    }

    loadRefund()
  }, [isEditMode, branchId, id, refundFromList])

  // Note: Bill lines are loaded and auto-filled in handleBillChange

  // Lines are auto-filled when Bill is selected (in handleBillChange)

  // Helper function to auto-fill refund lines from bill lines (Full Refund - all fields)
  const autoFillLinesFromBill = (billLinesData: RefundBillLine[]): RefundLine[] => {
    console.log('🟢 [autoFillLinesFromBill] Input billLinesData:', billLinesData)
    console.log('🟢 [autoFillLinesFromBill] Available items:', items.length)
    console.log('🟢 [autoFillLinesFromBill] Available taxRates:', taxRates.length)
    
    const newLines = billLinesData.map((billLine, index) => {
      console.log(`🟢 [autoFillLinesFromBill] Processing line ${index}:`, billLine)
      
      // Map fields from bill line to refund line
      // Use exact field names from API response
      const billLineId = billLine.lineId || billLine.billLineId || billLine.id || 0
      const itemId = billLine.itemId || 0
      const description = billLine.description || ''
      const qty = billLine.qty || billLine.remainingQty || 0
      const unitPrice = billLine.unitPrice || 0
      const itemPrice = billLine.itemPrice || (qty * unitPrice)
      const taxRateId = billLine.taxRateId || 0
      const taxRate = billLine.taxRate || billLine.taxPercent || 0
      const taxAmount = billLine.taxAmount || 0
      const lineAmount = billLine.lineAmount || itemPrice + taxAmount

      // Try to find item and tax rate for fallback values if needed
      const item = items.find((i) => (i.itemId || i.id) === itemId)
      const taxRateObj = taxRates.find((t) => (t.taxRateId || t.id) === taxRateId)

      console.log(`🟢 [autoFillLinesFromBill] Line ${index} - Found item:`, item)
      console.log(`🟢 [autoFillLinesFromBill] Line ${index} - Found taxRate:`, taxRateObj)

      // Use description from bill line, fallback to item name if empty
      const finalDescription = description || item?.itemNameEn || item?.itemNameAr || item?.itemName || ''
      
      // Use taxRate from bill line, fallback to taxRate object if needed
      const finalTaxRate = taxRate || taxRateObj?.taxPercent || taxRateObj?.taxRate || 0
      
      // Recalculate if values are missing
      const finalItemPrice = itemPrice || (qty * unitPrice)
      const finalTaxAmount = taxAmount || (finalItemPrice * (finalTaxRate / 100))
      const finalLineAmount = lineAmount || (finalItemPrice + finalTaxAmount)

      const refundLine: RefundLine = {
        lineId: 0, // New line, will be set by backend
        billLineId: billLineId, // Map from bill line's lineId
        refundId: refund.refundId || 0,
        itemId: itemId, // From bill line
        description: finalDescription, // From bill line or item
        qty: qty, // From bill line
        unitPrice: unitPrice, // From bill line
        itemPrice: finalItemPrice, // From bill line or calculated
        taxRateId: taxRateId, // From bill line
        taxRate: finalTaxRate, // From bill line or taxRate object
        taxAmount: finalTaxAmount, // From bill line or calculated
        lineAmount: finalLineAmount, // From bill line or calculated
        creditAccount: 0, // Not in bill line, keep empty/0
        isDeleted: 'false',
      }
      
      console.log(`🟢 [autoFillLinesFromBill] Line ${index} - Generated refundLine:`, refundLine)
      return refundLine
    })
    
    console.log('🟢 [autoFillLinesFromBill] Generated newLines:', newLines)
    return newLines
  }


  const calculateLineTotals = (line: RefundLine): RefundLine => {
    const qty = line.qty || 0
    const unitPrice = line.unitPrice || 0
    const taxPercent = line.taxRate || 0

    const itemPrice = qty * unitPrice
    const taxAmount = itemPrice * (taxPercent / 100)
    const lineAmount = itemPrice + taxAmount

    return {
      ...line,
      itemPrice,
      taxAmount,
      lineAmount,
    }
  }

  const summaryTotals = useMemo(() => {
    const validLines = refund.refundLine?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    
    // Total = SubTotal + VAT
    const total = subTotal + totalTax

    return {
      subTotal,
      totalTax,
      total,
    }
  }, [refund.refundLine, deletedRowIndices])

  useEffect(() => {
    setRefund((prev) => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalTax: summaryTotals.totalTax,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  // Available bills (only those with remaining amount > 0 and with valid lines)
  const availableBills = useMemo(() => {
    return bills.filter((bill) => {
      const billId = bill.billId || bill.id
      const remaining = bill.remainingAmount || bill.total || bill.amount || 0
      const hasValidLines = billId != null && billsWithValidLines.has(billId)
      
      if (remaining <= 0) {
        return false
      }
      // Only filter by valid lines if we've finished checking (to avoid filtering while still checking)
      if (!checkingBillLines && !hasValidLines) {
        console.log('Filtering out bill without valid lines (all lines have qty=0 or amount=0):', billId)
        return false
      }
      return true
    })
  }, [bills, billsWithValidLines, checkingBillLines])


  const handleBillChange = async (value: string) => {
    const billId = Number(value) || 0
    
    if (billId === 0) {
      setRefund((prev) => ({
        ...prev,
        billId: 0,
        dueDate: '',
        refundLine: getDefaultRows(taxRates),
      }))
      setBillLines([])
      setOriginalBillLines(new Map())
      setRemainingAmount(0)
      setVendorName('')
      return
    }
    
    const bill = bills.find((b) => (b.billId || b.id) === billId)
    
    let vendorName = ''
    if (bill?.vendorId) {
      const vendor = vendors.find((v) => (v.vendorId || v.id) === bill.vendorId)
      vendorName = vendor?.vendorName || vendor?.vendorNameEn || vendor?.vendorNameAr || ''
    }
    
    setRemainingAmount(bill?.remainingAmount || bill?.total || bill?.amount || 0)
    setVendorName(vendorName)
    
    let loadedLines: RefundBillLine[] = []
    let billData: any = null
    
    if (branchId && billId) {
      try {
        const response = await refundsService.getBillLines(branchId, billId)
        const allLines = response.billsLines || []
        billData = response.bill || {}
        
        // Filter out lines with qty = 0 or amount = 0
        loadedLines = allLines.filter((line: RefundBillLine) => {
          const qty = line.qty || 0
          const lineAmount = line.lineAmount || 0
          return qty > 0 && lineAmount > 0
        })
        
        // Store original bill line values for validation (keyed by lineId which becomes billLineId)
        const originalValuesMap = new Map<number, { qty: number; unitPrice: number }>()
        loadedLines.forEach((line: RefundBillLine) => {
          // Use lineId as key (this will match billLineId in refund line)
          const lineId = line.lineId || line.billLineId || 0
          if (lineId > 0) {
            originalValuesMap.set(lineId, {
              qty: line.qty || 0,
              unitPrice: line.unitPrice || 0
            })
          }
        })
        setOriginalBillLines(originalValuesMap)
        setBillLines(loadedLines)
        
        if (loadedLines.length === 0) {
          await showError('No Data', 'No valid bill lines found for this bill (lines with qty=0 or amount=0 are excluded)')
        }
      } catch (error: any) {
        console.error('Failed to load bill lines', error)
        await showError('Error', error.response?.data?.message || 'Failed to load bill lines')
        setBillLines([])
        setOriginalBillLines(new Map())
      }
    }
    
    const newLines =
      loadedLines.length > 0 && items.length > 0 && taxRates.length > 0
        ? autoFillLinesFromBill(loadedLines)
        : getDefaultRows(taxRates)
    
    setRefund((prev) => ({
      ...prev,
      billId,
      dueDate: bill?.dueDate || billData?.dueDate || '',
      refundLine: newLines,
    }))
  }


  const handleLineChange = (index: number, field: keyof RefundLine, value: any) => {
    setRefund((prev) => {
      const newLines = [...(prev.refundLine || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      // Validation for QTY and UNIT PRICE - cannot exceed original bill line values
      if (field === 'qty' && updatedLine.billLineId && updatedLine.billLineId > 0) {
        const originalLine = originalBillLines.get(updatedLine.billLineId)
        if (originalLine && Number(value) > originalLine.qty) {
          showValidationError(`Quantity cannot exceed original bill quantity: ${originalLine.qty}`)
          return prev // Don't update if validation fails
        }
      }

      if (field === 'unitPrice' && updatedLine.billLineId && updatedLine.billLineId > 0) {
        const originalLine = originalBillLines.get(updatedLine.billLineId)
        if (originalLine && Number(value) > originalLine.unitPrice) {
          showValidationError(`Unit price cannot exceed original bill unit price: ${originalLine.unitPrice.toFixed(2)}`)
          return prev // Don't update if validation fails
        }
      }

      if (field === 'itemId' && value) {
        const item = items.find((i) => i.itemId === Number(value))
        if (item) {
          if (!updatedLine.unitPrice || updatedLine.unitPrice === 0) {
            updatedLine.unitPrice = item.unitPrice || 0
          }
          updatedLine.description = item.itemNameEn || item.itemNameAr || ''
        }
      }

      if (field === 'taxRateId' && value) {
        const taxRate = taxRates.find((t) => t.taxRateId === Number(value))
        if (taxRate) {
          const rateValue = taxRate.taxPercent || taxRate.taxRate || 0
          updatedLine.taxRate = rateValue
        }
      }

      newLines[index] = calculateLineTotals(updatedLine)
      
      if (remainingAmount > 0) {
        const totalLinesAmount = newLines
          .filter((_, idx) => !deletedRowIndices.has(idx))
          .reduce((sum, line) => sum + (line.lineAmount || 0), 0)
        if (totalLinesAmount > remainingAmount) {
          showValidationError(t('refunds.totalAmountCannotExceed', {
            total: totalLinesAmount.toFixed(2),
            remaining: remainingAmount.toFixed(2)
          }))
        }
      }

      return { ...prev, refundLine: newLines }
    })
  }


  const handleDeleteRow = (index: number) => {
    const line = refund.refundLine?.[index]
    if (line?.lineId && line.lineId > 0) {
      setDeletedRowIndices((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      setRefund((prev) => {
        const newLines = [...(prev.refundLine || [])]
        newLines.splice(index, 1)
        return { ...prev, refundLine: newLines }
      })
    }
  }

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('refunds.pleaseSelectBranch'))
      return
    }

    if (!refund.billId) {
      await showValidationError(t('refunds.pleaseSelectBill'))
      return
    }

    if (!refund.refundLine || refund.refundLine.length === 0) {
      await showValidationError(t('refunds.pleaseAddAtLeastOneItem'))
      return
    }

    const validLines = refund.refundLine.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      return !isDeleted && line.itemId && line.itemId > 0
    })

    if (validLines.length === 0) {
      await showValidationError(t('refunds.pleaseAddAtLeastOneValidItem'))
      return
    }

    if (remainingAmount > 0) {
      const totalAmount = summaryTotals.total
      if (totalAmount > remainingAmount) {
        await showValidationError(t('refunds.totalAmountCannotExceed', {
          total: totalAmount.toFixed(2),
          remaining: remainingAmount.toFixed(2)
        }))
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? t('refunds.updatingRefund') : t('refunds.savingRefund'),
        t('refunds.pleaseWait')
      )

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      // Prepare refund lines - filter out empty lines (only save lines with actual data)
      // Empty lines without itemId should not be saved
      const linesToSave: RefundLine[] = []
      refund.refundLine?.forEach((line, index) => {
        const isDeleted = deletedRowIndices.has(index)

        // For non-deleted lines: only include if they have actual data (itemId > 0)
        // Empty lines without itemId should not be saved
        if (!isDeleted && (!line.itemId || line.itemId === 0)) {
          return // Skip empty lines
        }

        const recalculatedLine = calculateLineTotals(line)

        linesToSave.push({
          ...recalculatedLine,
          refundId: refund.refundId || 0,
          isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
        })
      })

      const toApiDate = (value?: string): string => {
        if (!value) return new Date().toISOString()
        try {
          if (value.includes('T')) {
            return new Date(value).toISOString()
          }
          return new Date(`${value}T00:00:00`).toISOString()
        } catch {
          return new Date().toISOString()
        }
      }

      // Prepare payload matching exact API structure
      const payload: RefundVm = {
        refundId: refund.refundId || 0,
        refundDate: toApiDate(refund.refundDate),
        billId: refund.billId,
        dueDate: toApiDate(refund.dueDate),
        subTotal: summaryTotals.subTotal,
        totalTax: summaryTotals.totalTax,
        total: summaryTotals.total,
        debitAccount: refund.debitAccount || 0,
        isFullyRefund: 'false',
        note: refund.note || '',
        branchId: branchId,
        ipAddress: ipAddress, // Set logged-in user's IP address
        refundLine: linesToSave,
      }

      // Log payload to console
      console.group('🟢 [Refund Save] Payload Details - API Structure')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'INSERT')
      console.log('API Endpoint:', isEditMode ? '/api/ApiRefunds/Update' : '/api/ApiRefunds/Insert')
      console.log('IP Address (User):', ipAddress)
      console.log('')
      console.log('📦 Full Payload Object:')
      console.log(payload)
      console.log('')
      console.log('📋 Payload JSON (Exact API Structure):')
      console.log(JSON.stringify(payload, null, 2))
      console.log('')
      console.log('📋 Refund Lines (Empty lines filtered):')
      console.log('Total Lines:', linesToSave.length)
      console.log('Lines:', JSON.stringify(linesToSave, null, 2))
      console.groupEnd()

      if (isEditMode) {
        await refundsService.updateRefund(payload)
      } else {
        await refundsService.saveRefund(payload)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('refunds.refundUpdatedSuccessfully') : t('refunds.refundCreatedSuccessfully')
      )

      navigate('/purchases/refunds')
    } catch (error: any) {
      console.error('Failed to save refund', error)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Prepare options
  const billOptions: SearchableSelectOption[] = availableBills.map((bill) => ({
    value: String(bill.billId || bill.id || 0),
    label: `${bill.billNo || bill.billNumber || `Bill ${bill.billId || bill.id}`} - ${bill.vendorName || ''} (Remaining: ${bill.remainingAmount || bill.total || bill.amount || 0})`,
  }))

  const itemOptions: SearchableSelectOption[] = items.map((item) => ({
    value: String(item.itemId || item.id || 0),
    label: item.itemName || item.itemNameEn || item.itemNameAr || item.itemCode || `Item ${item.itemId || item.id}`,
  }))

  const taxRateOptions: SearchableSelectOption[] = taxRates.map((tax) => ({
    value: String(tax.taxRateId || tax.id || 0),
    label: `${tax.taxName || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((account) => {
    const accountId = account.accountId || account.id || 0
    const accountCode = account.accountCode || ''
    const accountName = account.accountName || account.accountNameEn || account.accountNameAr || ''
    const displayLabel = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(accountId),
      label: displayLabel,
    }
  })

  if (loading && !refund.refundLine?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('refunds.title')}
        description={isEditMode ? t('refunds.editVendorCreditNote') : t('refunds.createNewVendorCreditNote')}
      />

      <form onSubmit={(e) => { e.preventDefault(); handleSave() }}>
        {/* Refund Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('refunds.refundInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="refundDate">{t('refunds.refundDateRequired')}</Label>
                <Input
                  id="refundDate"
                  type="date"
                  value={refund.refundDate}
                  onChange={(e) => setRefund((prev) => ({ ...prev, refundDate: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="bill">{t('refunds.billRequired')}</Label>
                <SearchableSelect
                  options={billOptions}
                  value={refund.billId ? String(refund.billId) : ''}
                  onValueChange={handleBillChange}
                  placeholder={t('refunds.selectBill')}
                  searchPlaceholder={t('refunds.searchBills')}
                  emptyMessage={t('refunds.noBillsFound')}
                  disabled={saving || isEditMode}
                />
              </div>

              <div>
                <Label htmlFor="vendor">{t('refunds.vendor')}</Label>
                <Input
                  id="vendor"
                  value={vendorName}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div>
                <Label htmlFor="dueDate">{t('refunds.dueDate')}</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={refund.dueDate}
                  onChange={(e) => setRefund((prev) => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="remainingAmount">{t('refunds.remainingAmount')}</Label>
                <Input
                  id="remainingAmount"
                  value={remainingAmount.toFixed(2)}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items Section */}
        <Card>
          <CardHeader className="bg-primary/5">
            <CardTitle>{t('bills.items')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-primary/5">
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.item')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.descriptionRequired')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.qty')}</th>
                    <th className="p-3 text-left text-sm font-semibold border min-w-[250px]">{t('refunds.credAcc')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.unitPrice')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.itemPrice')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('refunds.taxRatePercent')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.taxAmount')}</th>
                    <th className="p-3 text-left text-sm font-semibold border">{t('bills.amount')}</th>
                    <th className="p-3 text-left text-sm font-semibold border w-[50px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {refund.refundLine?.map((line, index) => {
                    const isDeleted = deletedRowIndices.has(index)
                    return (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                          isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                        }`}
                      >
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <SearchableSelect
                              options={itemOptions}
                              value={line.itemId ? String(line.itemId) : ''}
                              onValueChange={(value) => handleLineChange(index, 'itemId', Number(value) || 0)}
                              placeholder="Select item"
                              searchPlaceholder="Search items..."
                              emptyMessage="No items found"
                              disabled={saving || isDeleted || !!(line.billLineId && line.billLineId > 0)}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              value={line.description || ''}
                              onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                              placeholder={t('refunds.description')}
                              disabled={saving || isDeleted}
                              className="min-w-[200px]"
                              readOnly={!!(line.billLineId && line.billLineId > 0)}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.qty || ''}
                              onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                              placeholder="0"
                              disabled={saving || isDeleted}
                              className={`w-20 ${(line.qty || 0) === 1 ? 'bg-muted' : ''}`}
                              readOnly={(line.qty || 0) === 1}
                              max={line.billLineId && line.billLineId > 0 ? originalBillLines.get(line.billLineId)?.qty : undefined}
                              title={line.billLineId && line.billLineId > 0 ? `Max: ${originalBillLines.get(line.billLineId)?.qty || 0}` : ''}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10 min-w-[250px]">
                            <SearchableSelect
                              options={accountOptions}
                              value={line.creditAccount ? String(line.creditAccount) : ''}
                              onValueChange={(value) => handleLineChange(index, 'creditAccount', Number(value) || 0)}
                              placeholder={t('bills.selectAccount')}
                              searchPlaceholder={t('bills.searchAccounts')}
                              emptyMessage={t('bills.noAccountsFound')}
                              disabled={saving || isDeleted}
                              className="w-full min-w-[250px]"
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.unitPrice || ''}
                              onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                              placeholder="0.00"
                              disabled={saving || isDeleted}
                              className={`w-24 ${(line.qty || 0) > 1 ? 'bg-muted' : ''}`}
                              readOnly={(line.qty || 0) > 1}
                              max={line.billLineId && line.billLineId > 0 ? originalBillLines.get(line.billLineId)?.unitPrice : undefined}
                              title={line.billLineId && line.billLineId > 0 ? `Max: ${originalBillLines.get(line.billLineId)?.unitPrice?.toFixed(2) || '0.00'}` : ''}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.itemPrice?.toFixed(2) || '0.00'}
                              disabled
                              className="w-24 bg-muted"
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <SearchableSelect
                              options={taxRateOptions}
                              value={line.taxRateId ? String(line.taxRateId) : ''}
                              onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value) || 0)}
                              placeholder="Select tax"
                              searchPlaceholder="Search tax rates..."
                              emptyMessage="No tax rates found"
                              disabled={saving || isDeleted}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.taxAmount?.toFixed(2) || '0.00'}
                              disabled
                              className="w-24 bg-muted"
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center h-10">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.lineAmount?.toFixed(2) || '0.00'}
                              disabled
                              className="w-24 bg-muted"
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle border">
                          <div className="flex items-center justify-center h-10">
                            {isDeleted ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRow(index)}
                                className="text-green-600 hover:text-green-700"
                              >
                                {t('common.undo')}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRow(index)}
                                disabled={saving}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-primary/5">
                    <td colSpan={5} className="p-3 text-right font-semibold border">
                      {t('bills.subTotal')}
                    </td>
                    <td className="p-3 text-right font-semibold border">
                      {summaryTotals.subTotal.toFixed(2)}
                    </td>
                    <td colSpan={2} className="p-3 text-right font-semibold border">
                      {t('bills.totalTax')}
                    </td>
                    <td className="p-3 text-right font-semibold border">
                      {summaryTotals.totalTax.toFixed(2)}
                    </td>
                    <td className="p-3 border"></td>
                  </tr>
                  <tr className="bg-primary/5">
                    <td colSpan={8} className="p-3 text-right font-semibold border">
                      {t('bills.netAmount')}
                    </td>
                    <td className="p-3 text-right font-semibold border">
                      {summaryTotals.total.toFixed(2)}
                    </td>
                    <td className="p-3 border"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('bills.notesRemarks')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={refund.note}
              onChange={(e) => setRefund((prev) => ({ ...prev, note: e.target.value }))}
              placeholder={t('bills.enterNotesRemarks')}
              rows={4}
              disabled={saving}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/purchases/refunds')} disabled={saving} className="w-full sm:w-auto">
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saving} className="w-full sm:w-auto min-w-[150px]">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('refunds.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditMode ? t('refunds.updateRefund') : t('refunds.saveRefund')}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

